import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCancelOrder, useCancelReasons, useItem } from '@/hooks/item';
import { CancelOrderModal } from '@/types/modal';
import { ErrorModal, LoadingBodyModal, StandardModal, SuccessModal } from '@/components/molecules/common/Modal';
import styles from './Modal.module.scss';
import { Dropdown, Input } from '../molecules/common/Form';

const OTHER_REASON_ID = '11';
const excluded_ids = ['7', '8', '10', '12'];
const View: React.FC<{ modal: CancelOrderModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { item, onClose } = modal;

  const itemR = useItem(item.productId, false);
  const submitR = useCancelOrder(item.productId);

  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');

  const cancelReasonData = useCancelReasons();

  const reasons = useMemo(() => {
    if (cancelReasonData.kind === 'Loaded') {
      return cancelReasonData.data.data
        .map((r) => ({
          value: r.id.toString(),
          label: r.reason,
        }))
        .filter((e) => {
          return !excluded_ids.includes(e.value);
        });
    }
    return [];
  }, [cancelReasonData.kind]);

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      itemR.refetch();
    }
  }, [submitR.kind]);

  if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.cancel_order.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    return (
      <SuccessModal
        title={t('modal.cancel_order.title')}
        message={t('modal.cancel_order.success')}
        onClose={onClose}
      />
    );
  }

  const body =
    submitR.kind === 'Loading' ? (
      <LoadingBodyModal height={40} />
    ) : (
      <div className={styles.updateItemComment}>
        <div className={styles.text1rem}>{`${t('common.item')} #${item.productId}`}</div>
        <div>{t('modal.cancel_order.description')}</div>
        <Dropdown
          required={true}
          label={t('modal.cancel_order.reason')}
          value={reason}
          onSelect={setReason}
          items={reasons}
        />
        {reason === OTHER_REASON_ID && (
          <Input
            required={true}
            label={t('modal.cancel_order.otherReason')}
            value={otherReason}
            multiline={true}
            rows={6}
            maxLength={1000}
            onChange={setOtherReason}
          />
        )}
      </div>
    );

  const isDisabled = !reason || (reason === OTHER_REASON_ID && !otherReason);

  const handleSubmit = () => {
    const comment = reason === OTHER_REASON_ID ? otherReason : undefined;
    submitR.mutate({ reasonId: reason, comment });
  };

  return (
    <StandardModal
      title={t('modal.cancel_order.title')}
      modalName="cancel_order"
      onClose={onClose}
      onSubmit={handleSubmit}
      submittable={!isDisabled}
      submitLabel={t('common.submit').toUpperCase()}
      body={body}
      containerStyle={{ width: 450 }}
    />
  );
};

export default View;
